import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import {
  collapseToRanges,
  prepareCodesForSubmit,
  RangeTooLargeError,
} from "helpers/codeRanges";
import useDocumentTitle from "hooks/useDocumentTitle";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useCreateChargeBasedPricingMutation,
  useGetChargeBasedPricingByIdQuery,
} from "store/queries/chargeBasedPricing";

import {
  ChargeBasedPricingForm,
  IChargeBasedPricingFormData,
  INITIAL_CHARGE_BASED_PRICING_FORM_DATA,
} from "./components/ChargeBasedPricingForm";

export function CopyChargeBasedPricingPage() {
  useDocumentTitle("Copy Charge-Based Pricing Rule");
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const asDefault = searchParams.get("asDefault") === "true";
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const { data, isLoading: isFetching } = useGetChargeBasedPricingByIdQuery(
    id ?? ""
  );
  const [createMutation, { isLoading: isCreating }] =
    useCreateChargeBasedPricingMutation();
  const [formData, setFormData] = useState<IChargeBasedPricingFormData>(
    INITIAL_CHARGE_BASED_PRICING_FORM_DATA
  );

  // A copy is a new scoped rule by default (only one default row may exist), so scope is
  // dropped unless the caller explicitly asks to copy as the default via ?asDefault=true.
  useEffect(() => {
    const ruleset = data?.data;
    if (!ruleset) return;
    setFormData({
      scope: ruleset.scope,
      multiplier: Number(ruleset.multiplier),
      maxAmountInDollars:
        ruleset.maxAmountInCents === null ||
        ruleset.maxAmountInCents === undefined
          ? null
          : ruleset.maxAmountInCents / 100,
      providerId: asDefault ? null : ruleset.providerId,
      payerId: asDefault ? null : ruleset.payerId,
      codes: collapseToRanges(ruleset.codes ?? []),
      excludedCodes: collapseToRanges(ruleset.excludedCodes ?? []),
      groupName: ruleset.groupName ?? "",
      isDefault: asDefault,
    });
  }, [data, asDefault]);

  const handleSubmit = async () => {
    if (!formData.scope) return;
    if (!formData.isDefault && !formData.providerId) return;
    let codes: string[];
    let excludedCodes: string[];
    try {
      codes = prepareCodesForSubmit(formData.codes);
      excludedCodes = prepareCodesForSubmit(formData.excludedCodes);
    } catch (err) {
      toastElement?.show({
        severity: "error",
        summary: "Range too large",
        detail:
          err instanceof RangeTooLargeError
            ? err.message
            : "Failed to expand code ranges",
      });
      return;
    }
    setIsLoading(true);
    try {
      await createMutation({
        scope: formData.scope,
        multiplier: formData.multiplier,
        maxAmountInCents:
          formData.maxAmountInDollars === null
            ? null
            : Math.round(formData.maxAmountInDollars * 100),
        codes,
        excludedCodes,
        groupName: formData.groupName.trim() || null,
        isDefault: formData.isDefault,
        providerId: formData.isDefault ? null : formData.providerId,
        payerId: formData.isDefault ? null : formData.payerId,
      }).unwrap();
      toastElement?.show({
        severity: "success",
        summary: "Created!",
        detail: "Charge-Based Pricing rule copied successfully.",
      });
      navigate("/payer-rules");
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail:
          error?.data?.message || "Failed to copy charge-based pricing rule",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingSpinner />;

  return (
    <ChargeBasedPricingForm
      title="Copy Charge-Based Pricing Rule"
      submitLabel="Create Copy"
      formData={formData}
      onChange={setFormData}
      onSubmit={handleSubmit}
      isSubmitting={isCreating}
    />
  );
}
